import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useIsCompactViewport } from "@/lib/breakpoints";
import { cn } from "@/lib/utils";

// Aiguilleur Drawer mobile (<770) / Dialog desktop (>=770).
// Meme API que Dialog shadcn — facilite la migration des modales existantes.
//
// Pattern de scroll interne (modales avec formulaire long ou contenu > 80vh) :
// ne PAS mettre `overflow-y-auto` sur le ResponsiveDialogContent (vaul Drawer
// detecte le scroll et bloque le swipe-to-close, comportement non desire). Au
// lieu de ca, utiliser le pattern flex-col avec un body interne scrollable :
//
//   <ResponsiveDialogContent className="max-h-[85vh] overflow-hidden flex flex-col">
//     <ResponsiveDialogHeader>...</ResponsiveDialogHeader>
//     <div className="flex-1 overflow-y-auto min-h-0">
//       {body avec scroll}
//     </div>
//     <ResponsiveDialogFooter>...</ResponsiveDialogFooter>
//   </ResponsiveDialogContent>
//
// `min-h-0` est crucial sur le body (sinon flex item refuse de retrecir sous
// son contenu et le scroll ne s'active pas). Le footer reste fixe en bas, le
// body scrolle independamment, le swipe-to-close vaul fonctionne correctement.

interface ResponsiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

export function ResponsiveDialog({ open, onOpenChange, children }: ResponsiveDialogProps) {
  const isMobile = useIsCompactViewport();

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        {children}
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children}
    </Dialog>
  );
}

interface ResponsiveDialogContentProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Class appliquee au DialogContent (desktop) uniquement. Drawer prend la pleine largeur. */
  desktopClassName?: string;
  /** Surcharge le style du bouton X natif de DialogContent (desktop). Drawer
   *  n'a pas de X natif (gestes de fermeture). */
  closeClassName?: string;
}

export function ResponsiveDialogContent({
  className,
  desktopClassName,
  closeClassName,
  children,
  ...props
}: ResponsiveDialogContentProps) {
  const isMobile = useIsCompactViewport();

  if (isMobile) {
    // DrawerContent shadcn n'a aucun padding par defaut, contrairement a
    // DialogContent qui porte `p-6`. Sans padding latera, le body d'une modale
    // (ex : `<div className="py-4">...</div>`) se retrouve colle aux bords du
    // drawer sur mobile. On applique `px-4 pb-4` par defaut : `px-4` aligne le
    // body sur les DrawerHeader/Footer (qui ont deja `p-4`), `pb-4` garantit
    // un espace sous le footer (ou sous le body s'il n'y a pas de footer). Pas
    // de `pt-` pour ne pas decaler le handle vaul (`mt-4` interne au Drawer).
    // Le caller peut override via `className` (twMerge gere les conflits).
    return (
      <DrawerContent className={cn("max-h-[90vh] px-4 pb-4", className)} {...props}>
        {children}
      </DrawerContent>
    );
  }

  return (
    <DialogContent className={cn(desktopClassName, className)} closeClassName={closeClassName} {...props}>
      {children}
    </DialogContent>
  );
}

export function ResponsiveDialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const isMobile = useIsCompactViewport();
  return isMobile ? (
    <DrawerHeader className={className} {...props} />
  ) : (
    <DialogHeader className={className} {...props} />
  );
}

export function ResponsiveDialogTitle({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogTitle>) {
  const isMobile = useIsCompactViewport();
  return isMobile ? (
    <DrawerTitle className={className} {...props} />
  ) : (
    <DialogTitle className={className} {...props} />
  );
}

export function ResponsiveDialogDescription({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogDescription>) {
  const isMobile = useIsCompactViewport();
  return isMobile ? (
    <DrawerDescription className={className} {...props} />
  ) : (
    <DialogDescription className={className} {...props} />
  );
}

export function ResponsiveDialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const isMobile = useIsCompactViewport();
  return isMobile ? (
    <DrawerFooter className={className} {...props} />
  ) : (
    <DialogFooter className={className} {...props} />
  );
}
